/**
 * Utilidad para convertir URLs de Supabase Storage a URLs del proxy de Cloudflare
 */
import React from 'react';
import { 
  SUPABASE_URL, 
  CLOUDFLARE_PROXY_URL, 
  SUPABASE_STORAGE_BASE_URL,
  ERROR_MESSAGES,
  IMAGE_CONFIG
} from './config';
import { getImageProxyMonitor } from './imageProxyMonitoring';

// URL del worker por defecto en caso de que no esté configurada
// ✅ USAR LA URL CORRECTA DEL WORKER FUNCIONAL
const DEFAULT_WORKER_URL = 'https://disolgua-images.mynordavidruano2003.workers.dev';

/**
 * Determina si una URL es de Supabase Storage
 * @param url - URL a verificar
 * @returns true si es una URL de Supabase Storage
 */
export function isSupabaseStorageUrl(url: string): boolean {
  if (!url) return false;
  
  // Verificar si contiene la parte de storage de Supabase
  const isSupabaseUrl = url.includes('/storage/v1/object/public/') || 
                        url.includes(SUPABASE_URL) ||
                        url.includes('taheqwabfahvzniabmhh.supabase.co');
  
  return isSupabaseUrl;
}

/**
 * Extrae el bucket y la ruta del archivo de una URL de Supabase Storage
 * @param url - URL de Supabase Storage
 * @returns Un objeto con el bucket y la ruta, o null si no es una URL válida
 */
export function extractBucketAndPath(url: string): { bucket: string, path: string } | null {
  if (!url || !isSupabaseStorageUrl(url)) {
    return null;
  }

  try {
    // Ejemplo de URL de Supabase: 
    // https://taheqwabfahvzniabmhh.supabase.co/storage/v1/object/public/public/slider/imagen.webp
    const storagePathIndex = url.indexOf('/storage/v1/object/public/');
    
    if (storagePathIndex === -1) {
      // Intenta extraer de otras formas posibles (URLs resumidas, etc.)
      return null;
    }
    
    // Extraer la parte después de 'public/'
    const pathPart = url.substring(storagePathIndex + '/storage/v1/object/public/'.length);
    
    // Dividir en bucket y ruta
    const firstSlashIndex = pathPart.indexOf('/');
    
    if (firstSlashIndex === -1) {
      // No hay slash después del bucket
      return {
        bucket: pathPart,
        path: ''
      };
    }
    
    const bucket = pathPart.substring(0, firstSlashIndex);
    const path = pathPart.substring(firstSlashIndex + 1);
    
    return { bucket, path };
  } catch (error) {
    return null;
  }
}

/**
 * Convierte una URL de Supabase Storage a URL del proxy de Cloudflare
 * @param url - URL de Supabase Storage o cualquier URL
 * @returns URL del proxy de Cloudflare si es posible, o la URL original
 */
export function getProxiedImageUrl(url: string): string {
  // ✅ Worker funcionando correctamente
  
  // Si no hay URL o no es de Supabase, devolver la original
  if (!url || !isSupabaseStorageUrl(url)) {
    return url;
  }

  // Usar la URL del proxy configurada o la URL por defecto
  const proxyUrl = CLOUDFLARE_PROXY_URL || DEFAULT_WORKER_URL;
  
  // Verificar que la URL termine sin barra para construir correctamente
  const cleanProxyUrl = proxyUrl.endsWith('/') ? proxyUrl.slice(0, -1) : proxyUrl;

  try {
    const parts = extractBucketAndPath(url);
    
    if (!parts) {
      return url;
    }
    
    const { bucket, path } = parts;
    
    // Construir la URL del proxy
    // Formato: https://imageproxy.yourdomain.com/[bucket]/[path]
    const proxiedUrl = `${cleanProxyUrl}/${bucket}/${path}`;
    
    return proxiedUrl;
  } catch (error) {
    return url;
  }
}

/**
 * Componente de imagen que usa automáticamente el proxy para URLs de Supabase
 * @param props - Propiedades estándar de la etiqueta img de HTML
 * @returns Elemento img con la URL convertida si es necesario
 */
export function ProxyImage(props: React.ImgHTMLAttributes<HTMLImageElement>): JSX.Element {
  const { src: originalSrc, onError: onUserError, onLoad: onUserLoad, ...otherProps } = props;
  const monitor = getImageProxyMonitor();
  
  // Si no hay src, usar un placeholder
  if (!originalSrc) {
    return React.createElement('img', {
      src: IMAGE_CONFIG.DEFAULT_PLACEHOLDER,
      alt: otherProps.alt || "Imagen no disponible",
      ...otherProps
    });
  }
  
  // Convertir la URL si es de Supabase - memoizar para evitar recálculos
  const proxiedSrc = React.useMemo(() => getProxiedImageUrl(originalSrc), [originalSrc]);
  const loadStartTime = React.useRef<number>(0);

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const loadTime = Date.now() - loadStartTime.current;
    
    // Registrar evento de carga exitosa
    monitor.recordImageLoad({
      url: originalSrc,
      wasProxied: isSupabaseStorageUrl(originalSrc) && proxiedSrc !== originalSrc,
      loadTime,
      success: true,
      timestamp: Date.now()
    });
    
    // Llamar al handler del usuario si existe
    if (onUserLoad) {
      onUserLoad(e);
    }
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const imgElement = e.target as HTMLImageElement;
    
    // Si la URL que falló es la del proxy y es diferente de la original,
    // y la original es una URL de Supabase válida.
    if (imgElement.src === proxiedSrc && proxiedSrc !== originalSrc && isSupabaseStorageUrl(originalSrc)) {
      // ❌ ELIMINAR FALLBACK DIRECTO A SUPABASE - CAUSA DOBLE EGRESS
      // En lugar de hacer fallback a Supabase directo, usar placeholder
      console.warn('[ProxyImage] Worker proxy falló, usando placeholder en lugar de Supabase directo para evitar egress');
      imgElement.src = IMAGE_CONFIG.DEFAULT_PLACEHOLDER;
      
      // Registrar fallo del proxy
      monitor.recordImageLoad({
        url: originalSrc,
        wasProxied: true,
        loadTime: Date.now() - loadStartTime.current,
        success: false,
        timestamp: Date.now(),
        errorType: 'proxy_failure'
      });
    } else if (onUserError) {
      // Si la URL que falló es la original (lo que significa que el fallback también falló),
      // o si no era una URL de proxy para empezar (por ejemplo, proxiedSrc === originalSrc),
      // o si la original no era una URL de Supabase, entonces llamamos al manejador de error del usuario.
      onUserError(e);
    } else {
      // Si no hay onUserError proporcionado por el usuario, usar placeholder
      imgElement.src = IMAGE_CONFIG.DEFAULT_PLACEHOLDER;
    }
  };

  // Iniciar timer de carga solo cuando realmente cambia la URL
  React.useEffect(() => {
    loadStartTime.current = Date.now();
  }, [proxiedSrc]);
  
  return React.createElement('img', {
    src: proxiedSrc, // Siempre intentar la URL del proxy primero
    loading: otherProps.loading || (IMAGE_CONFIG.LAZY_LOADING ? "lazy" : undefined),
    ...otherProps,
    onLoad: handleLoad,
    onError: handleError // Usar el manejador de errores compuesto
  });
}

export default {
  getProxiedImageUrl,
  isSupabaseStorageUrl,
  extractBucketAndPath,
  ProxyImage
}; 